import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { Box, Tooltip, useTheme } from '@mui/material';

// assets
import { Info } from 'lucide-react';

// project imports
import {
    buildYearTabs,
    getActiveIndex,
    getSectionTabs,
    tabHref
} from 'menu-items/sectionTabs';
import ScrollRail, { PageHeader, pillSx } from 'ui-component/extended/ScrollRail';
import { fetchEvents, selectQicEventsData, selectQicYearOptions } from 'store/slices/qic';
import { useSelector } from 'store';

/**
 * Section chrome — the heading and its tab strip, rendered once by MainLayout
 * above <Outlet/>.
 *
 * Navigation-only for the section's own row: every one of its tabs is a <Link> to
 * a route that already exists, which is why this renders nothing at all on routes
 * outside the section map (Sector, Admin and every non-section page).
 *
 * The card, the corner ornament, the badge, the heading and the pills all come
 * from ui-component/extended/ScrollRail, which the admin strip renders too — so
 * the two strips are one strip. This file now holds only what is specific to a
 * section: which tabs belong to the page you are on, and what happens when the
 * section's tabs come from the events API rather than from a list.
 *
 * Community used to be the one section whose row mixed two kinds of tab: three
 * views of a single page told apart by `?tab=`, sitting beside a page of its own.
 * Those views now live in the module's own page, which renders them as a tab
 * strip of its own (see ModulePage), so every row this file draws is a plain list
 * of destinations — Community's two being Dashboard and Events.
 */

/**
 * One row of pills: the rail and the tabs in it, with the active one filled.
 *
 * A component rather than an inline map because the pills carry a BETA sup, a
 * live dot and an info glyph, and a second copy of that markup would quietly lose
 * them.
 *
 * Module scope, deliberately. Defined inside SectionTabs it would be a new
 * component type on every render, so React would unmount and remount every pill
 * whenever you navigated — the same trap navGlyph exists to avoid in
 * dhruvaMain.js.
 */
const TabRail = ({ tabs, active, label }) => {
    const theme = useTheme();

    return (
        <ScrollRail label={label} activeKey={active}>
            {tabs.map((tab, i) => {
                const on = i === active;
                const Icon = tab.icon;
                // Skipped for the first pill and for the one right after the
                // active pill, so the rule never sits beside a filled pill.
                // Computed here because it depends on the row, not on the pill.
                //
                // `active` can be -1: a page in this section but in no
                // destination this row lists, which is the /Events and /TFMasters
                // redirect frame — those views have left the row and the module's
                // own strip is what lights them. Nothing fills and no pill is
                // skipped, which is the right reading of it.
                const divider = i > 0 && !on && i - 1 !== active;
                return (
                    <Box
                        // Keyed by the href, not the path: the module's three tabs
                        // share one path and are told apart by their query, so a
                        // path-keyed list would hand React three children with the
                        // same key. Two tabs sharing an href really are the same
                        // destination, which is what makes this safe.
                        key={`${tab.label}@${tabHref(tab)}`}
                        component={Link}
                        to={tabHref(tab)}
                        role="tab"
                        aria-selected={on}
                        // Tells the rail which pill to bring into view.
                        data-rail-active={on ? 'true' : undefined}
                        sx={pillSx(theme, on, divider)}
                    >
                        {Icon && <Icon size={20} strokeWidth={2} aria-hidden="true" />}
                        {tab.label}
                        {/* The championship running right now. */}
                        {tab.live && (
                            <Box
                                component="span"
                                title="Current championship"
                                data-testid="live-year"
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    ml: '-4px',
                                    bgcolor: theme.palette.success.main
                                }}
                            />
                        )}
                        {tab.beta && (
                            <Box
                                component="sup"
                                sx={{
                                    color: on ? theme.palette.common.white : theme.palette.error.main,
                                    fontSize: '9.5px',
                                    fontWeight: 700,
                                    ml: '-6px'
                                }}
                            >
                                BETA
                            </Box>
                        )}
                        {/* What kind of message this tab takes, for the sections
                            whose labels do not say it — Help, where the four
                            entries are four ways of asking for something.

                            Hover/focus-only and `aria-hidden` on the glyph: the
                            pill is a link, and a focusable control nested in one
                            is invalid, so the description reaches a pointer rather
                            than a tab stop. The link's own name stays the label.

                            The tooltip portals, so the rail's `overflow` cannot
                            clip it. `ml` cancels part of the pill's 8px gap, the
                            way the BETA sup above does. */}
                        {tab.info && (
                            <Tooltip title={tab.info} placement="top" arrow>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        ml: '-3px',
                                        cursor: 'help',
                                        color: 'inherit',
                                        opacity: on ? 0.85 : 0.6,
                                        transition: 'opacity .2s'
                                    }}
                                >
                                    <Info size={15} strokeWidth={2.2} aria-hidden="true" />
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                );
            })}
        </ScrollRail>
    );
};

const SectionTabs = () => {
    const { pathname, search } = useLocation();

    const section = getSectionTabs(pathname, search);

    // QIC's tabs are the event years in the database, so they come from the same
    // slice the QIC pages use — one implementation of "which years exist".
    //
    // Both hooks sit above the `return null`, because a hook cannot hide behind it.
    // The fetch is gated on the section needing years, and the length check keeps it
    // to one fetch per session (the QIC pages call the same action).
    //
    // CALL it, do not dispatch it. fetchEvents is async and dispatches its own
    // setEventsData through the slice's imported `dispatch`; handing the returned
    // Promise to a store dispatch would throw in redux-thunk / RTK.
    const yearOptions = useSelector(selectQicYearOptions);
    // The same fetch stores the event rows, which carry the `status` saying which
    // championship is running.
    const eventsData = useSelector(selectQicEventsData);
    const needsYears = Boolean(section && section.yearTabs);
    useEffect(() => {
        if (needsYears && yearOptions.length === 0) fetchEvents();
    }, [needsYears, yearOptions.length]);

    if (!section) return null;

    // `pathname` goes in so the year tabs point at the page you are on.
    const tabs = section.yearTabs ? buildYearTabs(section, yearOptions, pathname, eventsData) : section.tabs;
    // Resolved against the tabs actually on screen, not the section's own.
    const active = getActiveIndex({ ...section, tabs }, pathname, search);
    const SectionIcon = section.icon;

    return (
        <PageHeader icon={SectionIcon} title={section.title} sub={section.sub}>
            {/* While the years are in flight the heading renders without a tab row,
                which is also what the design does with an empty one
                (`.tabs:empty{display:none}`). */}
            {tabs.length > 0 && <TabRail tabs={tabs} active={active} label={section.title} />}
        </PageHeader>
    );
};

export default SectionTabs;
